import React, { useCallback, useMemo, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Container,
  Grid,
  InputAdornment,
  IconButton,
  LinearProgress,
  Stack,
  TextField,
  Typography,
  alpha,
  keyframes,
  useTheme,
} from "@mui/material";
import {
  AutoAwesome,
  BoltOutlined,
  CheckCircleOutline,
  EmailOutlined,
  ExpandMore,
  LockOutlined,
  PersonOutlined,
  SmartToyOutlined,
  TrendingUpOutlined,
  Visibility,
  VisibilityOff,
  ArrowForwardOutlined,
} from "@mui/icons-material";
import { Link, useHistory, useLocation } from "react-router-dom";
import Header from "../../frontend/components/Header";
import FooterComp from "../../frontend/components/FooterComp";
import Pricing from "../../frontend/components/Pricing";
import AuthTurnstile from "./AuthTurnstile";
import UserLoginGoogle from "./UserLoginGoogle";
import { GlobalContext } from "../../context/GlobalContext";
import { TranslateContext } from "../../context/TranslateContext";
import { completeUserAuth, safeCheckoutReturn } from "../../utils/postLoginRedirect";
import {
  captureReferralFromUrl,
  clearStoredReferralCode,
  getStoredReferralCode,
} from "../../utils/referralAttribution";
import { planNum } from "../../utils/planFields";
import {
  formatPlanPriceInDisplay,
  resolvePlanDisplayCurrency,
} from "../../utils/planDisplayCurrency";

const shimmerBtn = keyframes`
  0% { background-position: -200% center; }
  100% { background-position: 200% center; }
`;

function passwordStrength(pw) {
  if (!pw) return { score: 0, label: "", color: "grey" };
  let score = 0;
  if (pw.length >= 6) score += 1;
  if (pw.length >= 10) score += 1;
  if (/[A-Z]/.test(pw) && /[a-z]/.test(pw)) score += 1;
  if (/\d/.test(pw)) score += 1;
  if (/[^A-Za-z0-9]/.test(pw)) score += 1;
  if (score <= 1) return { score: 25, label: "Weak", color: "error" };
  if (score <= 3) return { score: 55, label: "Fair", color: "warning" };
  if (score <= 4) return { score: 80, label: "Good", color: "info" };
  return { score: 100, label: "Strong", color: "success" };
}

const valueCards = [
  {
    icon: AutoAwesome,
    title: "All your creative tools in one place",
    text: "Generate images, videos, characters, music, clips and social content without switching platforms.",
  },
  {
    icon: BoltOutlined,
    title: "Credits that never expire",
    text: "Buy once and create at your pace. Unused credits stay in your balance until you use them.",
  },
  {
    icon: TrendingUpOutlined,
    title: "Built for creators who sell",
    text: "Ship ads, storytelling content and client work faster so you can earn more from every campaign.",
  },
];

const faqs = [
  [
    "How do I get started?",
    "Click Get Started on a plan. You’ll go to checkout, create your account if needed, then pay to unlock the studio.",
  ],
  [
    "Do I need a paid plan?",
    "Yes. Beyund AI is paid-only. Choose a credit package, create your account, and complete checkout.",
  ],
  [
    "Can I change plans later?",
    "Yes. You can buy another credit package any time. New credits are added to your existing balance.",
  ],
  [
    "Do unused credits expire?",
    "No. Purchased credits remain available until you use them.",
  ],
];

const UserSignup = () => {
  const theme = useTheme();
  const history = useHistory();
  const location = useLocation();
  const { hitAxios, showSnack } = React.useContext(GlobalContext);
  const { lang } = React.useContext(TranslateContext);

  const authQuery = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );
  const checkoutPath = safeCheckoutReturn(authQuery.get("returnTo"));
  const needsAccount = Boolean(checkoutPath);

  const [web, setWeb] = useState({});
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [displayCurrency, setDisplayCurrency] = useState({
    symbol: "₦",
    code: "NGN",
    rate: 1550,
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [registerMeta, setRegisterMeta] = useState({
    turnstile_site_key: null,
    min_form_seconds: 3,
    form_started_at: null,
  });
  const [turnstileToken, setTurnstileToken] = useState("");
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
    website: "",
  });

  const registerStartedAt = React.useRef(null);
  const formStartedToken = React.useRef(null);
  const strength = useMemo(() => passwordStrength(form.password), [form.password]);
  const referralCode = getStoredReferralCode();

  const fetchRegisterMeta = useCallback(async () => {
    try {
      const res = await hitAxios({
        path: "/api/user/register_meta",
        post: false,
        admin: false,
        showLoading: false,
        showSnackbar: false,
      });
      if (res?.data?.success && res.data.data) {
        setRegisterMeta({
          turnstile_site_key: res.data.data.turnstile_site_key || null,
          min_form_seconds: Number(res.data.data.min_form_seconds) || 3,
          form_started_at: res.data.data.form_started_at || null,
        });
        if (res.data.data.form_started_at) {
          formStartedToken.current = res.data.data.form_started_at;
        }
        registerStartedAt.current = Date.now();
      }
    } catch {
      /* optional */
    }
  }, [hitAxios]);

  React.useEffect(() => {
    captureReferralFromUrl();
    (async () => {
      const res = await hitAxios({
        path: "/api/web/get_web_public",
        post: false,
        admin: false,
        showLoading: false,
        showSnackbar: false,
      });
      if (res?.data?.success) setWeb(res.data.data || {});
    })();
    fetchRegisterMeta();
  }, [fetchRegisterMeta, hitAxios]);

  React.useEffect(() => {
    if (!checkoutPath) {
      setSelectedPlan(null);
      return;
    }
    const publicId = checkoutPath.split("/").pop();
    (async () => {
      const [planRes, gwRes] = await Promise.all([
        hitAxios({
          path: "/api/plan/get_all",
          post: false,
          admin: false,
          showLoading: false,
          showSnackbar: false,
        }),
        hitAxios({
          path: "/api/payment/active-gateways",
          post: false,
          admin: false,
          showLoading: false,
          showSnackbar: false,
        }),
      ]);
      if (gwRes?.data?.success) {
        const { currency } = resolvePlanDisplayCurrency(gwRes.data.data);
        setDisplayCurrency(currency);
      }
      if (!planRes?.data?.success) return;
      const raw = Array.isArray(planRes.data.plans)
        ? planRes.data.plans
        : planRes.data.data || [];
      const match = raw.find(
        (plan) =>
          String(plan.public_id || plan.publicId || "") === String(publicId),
      );
      if (match) setSelectedPlan(match);
    })();
  }, [checkoutPath, hitAxios]);

  const selectedPlanTitle = selectedPlan?.title || "";
  const selectedCredits = planNum(selectedPlan, "credits", "credits", 0);
  const selectedPrice = planNum(selectedPlan, "price", "price", 0);
  const selectedPriceLabel = selectedPlan
    ? `${displayCurrency.symbol || "₦"}${formatPlanPriceInDisplay(
        selectedPrice,
        displayCurrency,
      )}`
    : "";
  const googleLoginAvailable = Boolean(web?.google_login_id);

  React.useEffect(() => {
    if (!needsAccount) return;
    window.setTimeout(() => {
      document.getElementById("signup-account")?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 80);
  }, [needsAccount]);

  const openCloser = () => window.dispatchEvent(new Event("openLeadCloser"));
  const scrollToPlans = () =>
    document.getElementById("signup-plans")?.scrollIntoView({ behavior: "smooth" });

  async function handleSubmit() {
    if (!checkoutPath) {
      showSnack("Choose a plan first — click Get Started on a package below.", false);
      scrollToPlans();
      return;
    }

    const email = form.email.trim();
    const password = form.password;
    if (!email || !password || !form.name.trim()) {
      showSnack(
        lang?.authRegisterFieldsRequired || "Name, email and password are required",
        false,
      );
      return;
    }
    if (form.website?.trim()) return;

    const minMs = (registerMeta.min_form_seconds || 3) * 1000;
    const started = registerStartedAt.current;
    if (!started || Date.now() - started < minMs) {
      showSnack(
        lang?.authWaitBeforeRegister ||
          "Please wait a moment before creating your account.",
        false,
      );
      return;
    }

    if (registerMeta.turnstile_site_key && !turnstileToken) {
      showSnack(
        lang?.authCompleteSecurityCheck || "Please complete the security check.",
        false,
      );
      return;
    }
    if (form.password !== form.confirmPassword) {
      showSnack(lang?.authPasswordMismatch || "Passwords do not match", false);
      return;
    }
    if (password.length < 6) {
      showSnack(
        lang?.authPasswordMin || "Password must be at least 6 characters",
        false,
      );
      return;
    }

    setSubmitting(true);
    try {
      const res = await hitAxios({
        path: "/api/user/register",
        post: true,
        admin: false,
        obj: {
          name: form.name.trim(),
          email,
          password,
          website: form.website,
          form_started_at:
            formStartedToken.current ||
            registerMeta.form_started_at ||
            registerStartedAt.current,
          turnstile_token: registerMeta.turnstile_site_key
            ? turnstileToken || undefined
            : undefined,
          referral_code: getStoredReferralCode() || undefined,
        },
        showLoading: false,
      });

      if (res?.data?.success && res.data.token) {
        clearStoredReferralCode();
        try {
          await completeUserAuth({
            token: res.data.token,
            hitAxios,
            history,
            isNewAccount: true,
            returnTo: checkoutPath,
          });
        } catch {
          history.push(checkoutPath);
        }
      } else {
        if (res?.data?.msg) showSnack(res.data.msg, false);
        setTurnstileToken("");
        await fetchRegisterMeta();
      }
    } finally {
      setSubmitting(false);
    }
  }

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: 2.5,
      bgcolor: "background.paper",
    },
  };

  return (
    <Box
      onContextMenu={(event) => event.stopPropagation()}
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        bgcolor: "background.default",
      }}
    >
      <Header web={web} />
      <Box sx={{ height: { xs: 56, md: 64 } }} />

      <Box component="main" sx={{ pb: { xs: 2, md: 3 } }}>
        <Box
          sx={{
            position: "relative",
            overflow: "hidden",
            py: { xs: 5, md: 7 },
            background: `radial-gradient(circle at 18% 20%, ${alpha(
              theme.palette.primary.main,
              0.22,
            )}, transparent 40%), radial-gradient(circle at 82% 8%, ${alpha(
              "#db2777",
              0.16,
            )}, transparent 36%)`,
          }}
        >
          <Container maxWidth="md">
            <Stack spacing={2.5} alignItems="flex-start">
              <Typography
                variant="overline"
                color="primary"
                fontWeight={800}
                letterSpacing={1.2}
              >
                Get started with Beyund AI
              </Typography>
              <Typography
                variant="h3"
                fontWeight={900}
                sx={{ fontSize: { xs: "1.85rem", md: "2.6rem" }, lineHeight: 1.15 }}
              >
                {needsAccount
                  ? "Create your account to finish checkout"
                  : "Pick a plan, then unlock the full studio"}
              </Typography>
              <Typography
                color="text.secondary"
                sx={{ maxWidth: 640, fontSize: { xs: "1rem", md: "1.1rem" }, lineHeight: 1.7 }}
              >
                {needsAccount
                  ? `You’re almost there${selectedPlanTitle ? ` — ${selectedPlanTitle}` : ""}. Create your account below, then we’ll take you back to secure payment.`
                  : "Click Get Started on a plan to continue to checkout. New here? You’ll create your account next, then pay once to unlock images, video, characters, music and more."}
              </Typography>

              {!needsAccount && (
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                  <Button size="large" variant="contained" onClick={scrollToPlans}>
                    Choose a plan
                  </Button>
                  <Button
                    size="large"
                    variant="outlined"
                    startIcon={<SmartToyOutlined />}
                    onClick={openCloser}
                  >
                    Ask Beyund Assistant
                  </Button>
                </Stack>
              )}

              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={{ xs: 1, sm: 3 }}
                color="text.secondary"
              >
                {(needsAccount
                  ? ["Create account", "Complete payment", "Credits never expire"]
                  : ["1. Get Started on a plan", "2. Create account", "3. Pay & create"]
                ).map((item) => (
                  <Stack key={item} direction="row" spacing={0.7} alignItems="center">
                    <CheckCircleOutline color="success" fontSize="small" />
                    <Typography variant="body2">{item}</Typography>
                  </Stack>
                ))}
              </Stack>
            </Stack>
          </Container>
        </Box>

        {needsAccount && (
          <Box
            id="signup-account"
            sx={{
              scrollMarginTop: 88,
              py: { xs: 4, md: 5 },
              bgcolor: alpha(theme.palette.primary.main, 0.04),
            }}
          >
            <Container maxWidth="sm">
              <Stack spacing={2.5}>
                <Box textAlign="center">
                  <Typography variant="h4" fontWeight={900}>
                    Create your account
                  </Typography>
                  <Typography color="text.secondary" mt={1}>
                    After signup you’ll continue to secure checkout.
                  </Typography>
                </Box>

                {selectedPlan && (
                  <Box
                    sx={{
                      p: 2.25,
                      borderRadius: 3,
                      border: `1.5px solid ${theme.palette.primary.main}`,
                      bgcolor: "background.paper",
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="primary"
                      fontWeight={800}
                      letterSpacing={0.8}
                      textTransform="uppercase"
                    >
                      Selected plan
                    </Typography>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="flex-start"
                      mt={0.75}
                      spacing={2}
                    >
                      <Box>
                        <Typography variant="h5" fontWeight={900}>
                          {selectedPlanTitle}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" mt={0.4}>
                          {selectedCredits.toLocaleString()} credits · never expire
                        </Typography>
                      </Box>
                      <Typography variant="h5" fontWeight={900} color="primary.main">
                        {selectedPriceLabel}
                      </Typography>
                    </Stack>
                  </Box>
                )}

                {referralCode && (
                  <Alert severity="info">
                    You’re joining with a friend’s invite. Create your account to get started.
                  </Alert>
                )}

                {googleLoginAvailable && (
                  <UserLoginGoogle
                    hitAxios={hitAxios}
                    web={web}
                    returnTo={checkoutPath}
                  />
                )}

                <Box
                  component="input"
                  type="text"
                  name="website"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  value={form.website}
                  onChange={(e) => setForm({ ...form, website: e.target.value })}
                  sx={{
                    position: "absolute",
                    left: "-9999px",
                    width: "1px",
                    height: "1px",
                    opacity: 0,
                    pointerEvents: "none",
                    overflow: "hidden",
                  }}
                />

                <TextField
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  fullWidth
                  placeholder={lang?.authFullName || "Full name"}
                  sx={fieldSx}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonOutlined fontSize="small" color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
                <TextField
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  fullWidth
                  type="email"
                  placeholder={lang?.emailPlaceholder || "Email"}
                  sx={fieldSx}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailOutlined fontSize="small" color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
                <TextField
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  fullWidth
                  type={showPassword ? "text" : "password"}
                  placeholder={lang?.passwordPlaceholder || "Password"}
                  sx={fieldSx}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockOutlined fontSize="small" color="action" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                          size="small"
                        >
                          {showPassword ? (
                            <VisibilityOff fontSize="small" />
                          ) : (
                            <Visibility fontSize="small" />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                {form.password && (
                  <Box>
                    <Stack direction="row" justifyContent="space-between" mb={0.5}>
                      <Typography variant="caption" color="text.secondary">
                        Password strength
                      </Typography>
                      <Typography
                        variant="caption"
                        color={`${strength.color}.main`}
                        fontWeight={700}
                      >
                        {strength.label}
                      </Typography>
                    </Stack>
                    <LinearProgress
                      variant="determinate"
                      value={strength.score}
                      color={strength.color}
                      sx={{ height: 5, borderRadius: 99 }}
                    />
                  </Box>
                )}
                <TextField
                  value={form.confirmPassword}
                  onChange={(e) => setForm({ ...form, confirmPassword: e.target.value })}
                  fullWidth
                  type={showConfirm ? "text" : "password"}
                  placeholder={lang?.authConfirmPassword || "Confirm password"}
                  sx={fieldSx}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockOutlined fontSize="small" color="action" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowConfirm(!showConfirm)}
                          edge="end"
                          size="small"
                        >
                          {showConfirm ? (
                            <VisibilityOff fontSize="small" />
                          ) : (
                            <Visibility fontSize="small" />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                {registerMeta.turnstile_site_key && (
                  <AuthTurnstile
                    siteKey={registerMeta.turnstile_site_key}
                    onToken={(token) => setTurnstileToken(token || "")}
                    onExpire={() => setTurnstileToken("")}
                    onError={() => {}}
                  />
                )}

                <Button
                  onClick={handleSubmit}
                  disabled={submitting}
                  fullWidth
                  variant="contained"
                  size="large"
                  endIcon={!submitting && <ArrowForwardOutlined />}
                  sx={{
                    py: 1.6,
                    fontWeight: 800,
                    borderRadius: 2.5,
                    textTransform: "none",
                    background: `linear-gradient(90deg, ${theme.palette.primary.main}, #db2777, ${theme.palette.primary.main})`,
                    backgroundSize: "200% auto",
                    animation: submitting ? "none" : `${shimmerBtn} 4s linear infinite`,
                  }}
                >
                  {submitting
                    ? lang?.authPleaseWait || "Please wait…"
                    : "Create account & continue to checkout"}
                </Button>

                <Typography variant="body2" color="text.secondary" textAlign="center">
                  Already have an account?{" "}
                  <Box
                    component={Link}
                    to={`/user/login?returnTo=${encodeURIComponent(checkoutPath)}`}
                    sx={{
                      color: "primary.main",
                      fontWeight: 700,
                      textDecoration: "none",
                      "&:hover": { textDecoration: "underline" },
                    }}
                  >
                    Sign in
                  </Box>
                </Typography>

                <Button
                  variant="text"
                  size="small"
                  onClick={scrollToPlans}
                  sx={{ alignSelf: "center" }}
                >
                  Choose a different plan
                </Button>
              </Stack>
            </Container>
          </Box>
        )}

        {!needsAccount && (
          <Container maxWidth="lg" sx={{ mt: { xs: -2, md: -3 }, position: "relative", zIndex: 1 }}>
            <Grid container spacing={2}>
              {valueCards.map(({ icon: Icon, title, text }) => (
                <Grid item xs={12} md={4} key={title}>
                  <Box
                    sx={{
                      height: "100%",
                      p: 2.5,
                      borderRadius: 3,
                      bgcolor: "background.paper",
                      border: `1px solid ${theme.palette.divider}`,
                      boxShadow: theme.shadows[2],
                    }}
                  >
                    <Icon color="primary" />
                    <Typography fontWeight={800} mt={1.25} mb={0.6}>
                      {title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" lineHeight={1.7}>
                      {text}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Container>
        )}

        <Box id="signup-plans" sx={{ scrollMarginTop: 88, pt: { xs: 4, md: 5 } }}>
          <Container maxWidth="md" sx={{ textAlign: "center", mb: 1 }}>
            <Typography variant="overline" color="primary" fontWeight={800}>
              {needsAccount ? "Change plan" : "Plans"}
            </Typography>
            <Typography variant="h4" fontWeight={900} mt={1} sx={{ fontSize: { xs: "1.5rem", md: "2rem" } }}>
              {needsAccount
                ? "Want a different package?"
                : "Choose a plan to continue"}
            </Typography>
            <Typography color="text.secondary" mt={1} mb={1}>
              Click <strong>Get Started</strong> — you’ll go straight to checkout.
            </Typography>
          </Container>
          <Pricing showHeader={false} />
        </Box>

        {!needsAccount && (
          <Container maxWidth="md" sx={{ pt: { xs: 4, md: 5 }, pb: 0 }}>
            <Typography variant="h5" fontWeight={900} mb={1.5}>
              Common questions
            </Typography>
            {faqs.map(([question, answer]) => (
              <Accordion key={question} disableGutters elevation={0} sx={{ bgcolor: "transparent" }}>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography fontWeight={700}>{question}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography color="text.secondary" lineHeight={1.7}>
                    {answer}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            ))}
            <Stack alignItems="center" mt={3} mb={1}>
              <Button
                variant="outlined"
                startIcon={<SmartToyOutlined />}
                onClick={openCloser}
              >
                Ask Beyund Assistant
              </Button>
            </Stack>
            <Typography variant="body2" color="text.secondary" textAlign="center" mt={2}>
              Already have an account?{" "}
              <Box
                component={Link}
                to="/user/login"
                sx={{
                  color: "primary.main",
                  fontWeight: 700,
                  textDecoration: "none",
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                Sign in
              </Box>
            </Typography>
          </Container>
        )}
      </Box>

      <FooterComp />
    </Box>
  );
};

export default UserSignup;
